import { PercentageRow, HistoricalHoursRow, DayMapping } from '../types';
import { DAY_MAPPING, DEFAULT_PERCENTAGE_VALUES } from '../constants';

/**
 * Transform API data to PercentageRow format for daily percentage tables
 */
export const transformDailyPercentageData = (apiData: any[]): PercentageRow[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  console.log('Transforming daily percentage data:', apiData);

  const percentages = { ...DEFAULT_PERCENTAGE_VALUES };

  // Map API data to percentages
  apiData.forEach(item => {
    const dayKey = DAY_MAPPING[item.day_num as keyof typeof DAY_MAPPING];
    if (dayKey) {
      // Handle both percentage (0.1444) and percentage*100 (14.44) formats
      const defaultValue = item.daily_pct !== null && item.daily_pct !== undefined 
        ? (item.daily_pct <= 1 ? item.daily_pct * 100 : item.daily_pct).toFixed(2)
        : '0.00';
      
      // If override_daily_pct is null, copy the default value to override
      const overrideValue = item.override_daily_pct !== null && item.override_daily_pct !== undefined
        ? (item.override_daily_pct <= 1 ? item.override_daily_pct * 100 : item.override_daily_pct).toFixed(2)
        : defaultValue; // Copy default to override when override is null

      percentages[dayKey] = {
        default: `${defaultValue}%`,
        override: `${overrideValue}%`
      };
    }
  });

  // Calculate total (sum of override values or default if override is 0)
  let total = 0;
  Object.values(percentages).forEach(day => {
    const overrideValue = parseFloat(day.override.replace('%', ''));
    const defaultValue = parseFloat(day.default.replace('%', ''));
    // Use override value if it's greater than 0, otherwise use default
    total += overrideValue > 0 ? overrideValue : defaultValue;
  });

  const result = [{
    id: '1',
    percentages,
    total: `${total.toFixed(2)}%`
  }];

  console.log('Transformed percentage data result:', result);
  return result;
};

/**
 * Transform Historical Hours API data to table format
 */
export const transformHistoricalHoursData = (apiData: any[]): HistoricalHoursRow[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  console.log('Transforming historical hours data:', apiData);

  // Group data by VP_Zone
  const zoneData: { [key: string]: HistoricalHoursRow } = {};
  
  apiData.forEach(item => {
    const zone = item.VP_Zone;
    if (!zoneData[zone]) {
      zoneData[zone] = {
        id: zone.toLowerCase(),
        zone: zone,
        historicalHours: {
          sun: '', mon: '', tue: '', wed: '', thu: '', fri: '', sat: ''
        },
        fixedHours: {
          sun: '', mon: '', tue: '', wed: '', thu: '', fri: '', sat: ''
        }
      };
    }

    const dayKey = DAY_MAPPING[item.day_num as keyof typeof DAY_MAPPING];
    if (dayKey) {
      // Set historical hours (show as number if available, otherwise empty)
      const historicalValue = item.historical_hours != null 
        ? item.historical_hours.toString() 
        : '0';
      zoneData[zone].historicalHours[dayKey] = historicalValue;
      
      // Set fixed hours - if null in backend, copy historical_hours value
      zoneData[zone].fixedHours[dayKey] = item.fixed_hours != null 
        ? item.fixed_hours.toString() 
        : historicalValue; // Copy historical_hours when fixed_hours is null
      
      // Track which days have actual fixed_hours from backend (not null)
      if (!zoneData[zone].__actualFixedHours) {
        zoneData[zone].__actualFixedHours = {};
      }
      zoneData[zone].__actualFixedHours[dayKey] = item.fixed_hours !== null;
      
      // Initialize __changed metadata to track user modifications
      if (!zoneData[zone].__changed) {
        zoneData[zone].__changed = {};
      }
      zoneData[zone].__changed[dayKey] = false; // Initially no changes
    }
  });

  const result = Object.values(zoneData);
  console.log('Transformed historical hours data result:', result);
  return result;
};

/**
 * Transform Zone Distribution API data to table format
 */
export const transformZoneDistributionData = (apiData: any[], type: 'DSD' | 'NON-DSD'): any[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  console.log('Transforming zone distribution data:', apiData, 'for type:', type);

  const zoneGroups = apiData.reduce((acc, item) => {
    if (!item.VP_Zone) return acc;
    const zoneKey = item.VP_Zone.toLowerCase();
    if (!acc[zoneKey]) {
      acc[zoneKey] = [];
    }
    acc[zoneKey].push(item);
    return acc;
  }, {} as Record<string, any[]>);

  // Track which overrides are actual backend values (not null)
  const result = Object.entries(zoneGroups).map(([zoneId, zoneItems]) => {
    const zoneItemsArray = zoneItems as any[];
    let zoneName = zoneItemsArray[0]?.VP_Zone || zoneId.toUpperCase();

    const percentages = {
      sun: { default: '0.00%', override: '0.00%' },
      mon: { default: '0.00%', override: '0.00%' },
      tue: { default: '0.00%', override: '0.00%' },
      wed: { default: '0.00%', override: '0.00%' },
      thu: { default: '0.00%', override: '0.00%' },
      fri: { default: '0.00%', override: '0.00%' },
      sat: { default: '0.00%', override: '0.00%' },
    };
    
    let kcph = { default: '0.00', override: '0.00' };
    const actualOverrides: { [key: string]: boolean } = {};

    zoneItemsArray.forEach((item: any) => {
      const dayKey = DAY_MAPPING[item.day_num as keyof typeof DAY_MAPPING];
      if (dayKey) {
        // Handle percentage fields (default_pct and override_pct)
        let defaultValue = '0.00%';
        if (item.default_pct !== null && item.default_pct !== undefined) {
          const pctValue = (item.default_pct <= 1 ? item.default_pct * 100 : item.default_pct).toFixed(2);
          defaultValue = `${pctValue}%`;
          percentages[dayKey as keyof typeof percentages].default = defaultValue;
        }
        
        // If override_pct is null, copy default to override and mark as NOT an actual override
        // If override_pct has a value (including 0), use it and mark as actual override
        if (item.override_pct !== null && item.override_pct !== undefined) {
          const overridePctValue = (item.override_pct <= 1 ? item.override_pct * 100 : item.override_pct).toFixed(2);
          percentages[dayKey as keyof typeof percentages].override = `${overridePctValue}%`;
          actualOverrides[dayKey] = true; // Mark as actual override from backend
        } else {
          percentages[dayKey as keyof typeof percentages].override = defaultValue; // Copy default to override
          actualOverrides[dayKey] = false; // Not an actual override
        }

        // Handle cases fields (if they exist)
        if (item.default_cases !== null && item.default_cases !== undefined) {
          percentages[dayKey as keyof typeof percentages].default = `${item.default_cases}`;
        }
        
        if (item.override_cases !== null && item.override_cases !== undefined) {
          percentages[dayKey as keyof typeof percentages].override = `${item.override_cases}`;
          actualOverrides[dayKey] = true;
        } else if (item.default_cases !== null && item.default_cases !== undefined) {
          percentages[dayKey as keyof typeof percentages].override = `${item.default_cases}`;
          actualOverrides[dayKey] = false;
        }

        // Handle legacy daily_pct fields for backward compatibility
        if (item.daily_pct !== null && item.daily_pct !== undefined) {
          const pctValue = (item.daily_pct <= 1 ? item.daily_pct * 100 : item.daily_pct).toFixed(2);
          percentages[dayKey as keyof typeof percentages].default = `${pctValue}%`;
        }
        
        if (item.override_daily_pct !== null && item.override_daily_pct !== undefined) {
          const overridePctValue = (item.override_daily_pct <= 1 ? item.override_daily_pct * 100 : item.override_daily_pct).toFixed(2);
          percentages[dayKey as keyof typeof percentages].override = `${overridePctValue}%`;
          actualOverrides[dayKey] = true;
        }

        // Handle KCPH fields
        let defaultKcphValue = '0.00';
        if (item.kcph !== null && item.kcph !== undefined) {
          defaultKcphValue = item.kcph.toString();
          kcph.default = defaultKcphValue;
        }
        
        if (item.default_kcph !== null && item.default_kcph !== undefined) {
          defaultKcphValue = item.default_kcph.toString();
          kcph.default = defaultKcphValue;
        }
        
        // If override_kcph is null, copy default to override and mark as NOT an actual override
        // If override_kcph has a value (including 0), use it and mark as actual override
        if (item.override_kcph !== null && item.override_kcph !== undefined) {
          kcph.override = item.override_kcph.toString();
          actualOverrides['kcph'] = true; // Mark as actual override from backend
        } else {
          kcph.override = defaultKcphValue; // Copy default to override
          actualOverrides['kcph'] = false; // Not an actual override
        }
      }
    });

    return {
      id: zoneId,
      zoneName: zoneName,
      percentages,
      kcph,
      __actualOverrides: actualOverrides, // Track which overrides are from backend
    };
  });

  console.log('Transformed zone distribution data result:', result);
  return result;
};
